import React from 'react';
import { TrendingUp } from 'lucide-react';
import { FinancialValue } from '../shared/FinancialValue';

interface SensitivityAnalysisProps {
  data?: {
    parameter: string;
    results: {
      base_claim: number;
      range: {
        min: number;
        max: number;
        spread: number;
        spread_pct?: number;
      };
      results: Array<{ pct: number; claim: number }>;
    };
    run_at: string;
  };
  sensitivityParam: string;
  sensitivityLoading: boolean;
  onParamChange: (param: string) => void;
  onRunAnalysis: (param?: string) => void;
}

export const SensitivityAnalysisCard: React.FC<SensitivityAnalysisProps> = ({
  data,
  sensitivityParam,
  sensitivityLoading,
  onParamChange,
  onRunAnalysis,
}) => {
  const sa = data;
  const saBaseClaim = sa?.results?.base_claim;
  const saMin = sa?.results?.range?.min;
  const saMax = sa?.results?.range?.max;
  const saSpread = sa?.results?.range?.spread;
  const saSpreadPct = saBaseClaim > 0 ? (saSpread / saBaseClaim * 100) : 0;

  return (
    <div className="rounded-md p-6 border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="cs-h2 flex items-center gap-2">
          <TrendingUp size={22} />
          Sensitivity Analysis
        </h2>
        {sa && (
          <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>✓ Complete</span>
        )}
      </div>

      {/* Explanation */}
      <div className="rounded-md p-4 mb-4 bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          <strong>What is Sensitivity Analysis?</strong> This test shows how your claim amount would change if key inputs (like payroll or revenue) were different by ±20%. It helps demonstrate the robustness of your claim — a smaller spread means your calculation is more stable and reliable.
        </p>
      </div>
      
      {sa ? (
        <div className="space-y-4">
          {/* Results Display */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-md p-4 text-center bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-label)' }}>Parameter Tested</p>
              <p className="text-lg font-bold text-[var(--color-text-primary)]">{sa.parameter?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
            </div>
            <div className="rounded-md p-4 text-center bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-label)' }}>Base Claim</p>
              <p className="text-lg font-bold font-mono tabular-nums text-[var(--color-text-primary)]"><FinancialValue value={saBaseClaim} /></p>
            </div>
            <div className="rounded-md p-4 text-center bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-label)' }}>Spread</p>
              <p className="text-lg font-bold font-mono tabular-nums text-[var(--color-needs-human-text)]">{saSpreadPct.toFixed(1)}%</p>
            </div>
          </div>

          {/* Range visualization */}
          <div className="rounded-md p-4 bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
            <p className="text-sm mb-3 font-medium" style={{ color: 'var(--color-text-secondary)' }}>Claim Range (±20% variation)</p>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Min</p>
                <p className="text-sm font-bold font-mono tabular-nums text-[var(--color-needs-human-text)]"><FinancialValue value={saMin} /></p>
              </div>
              <div className="flex-1 relative">
                <div className="h-4 rounded-full bg-[var(--color-bg-base)] border border-[var(--color-border)]"></div>
                <div className="absolute left-1/2 top-0 w-1 h-4 rounded transform -translate-x-1/2" title="Base Claim" style={{ backgroundColor: 'var(--color-brand)' }}></div>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Max</p>
                <p className="text-sm font-bold font-mono tabular-nums text-[var(--color-needs-human-text)]"><FinancialValue value={saMax} /></p>
              </div>
            </div>
            <p className="text-xs mt-2 text-center" style={{ color: 'var(--color-text-muted)' }}>
              Total spread: <FinancialValue value={saSpread} /> • The center marker shows your base claim
            </p>
          </div>

          {/* Re-run buttons */}
          <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
            <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>Test a different parameter:</p>
            <div className="flex gap-2">
              {[
                { key: 'payroll', label: 'Payroll' },
                { key: 'revenue', label: 'Revenue' },
                { key: 'variable_expenses', label: 'Expenses' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => onRunAnalysis(key)}
                  disabled={sensitivityLoading}
                  className="flex-1 py-2 px-3 rounded-md text-sm font-medium disabled:opacity-50"
                  style={
                    sa.parameter === key
                      ? { backgroundColor: 'transparent', color: 'var(--color-brand)', border: '1px solid var(--color-brand)' }
                      : { backgroundColor: 'var(--color-bg-hover)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }
                  }
                >
                  {sensitivityLoading && sensitivityParam === key ? '...' : label}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Last run: {new Date(sa.run_at).toLocaleString()}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Select parameter to test:</label>
            <select
              value={sensitivityParam}
              onChange={(e) => onParamChange(e.target.value)}
              className="w-full p-3 rounded-md text-sm focus:ring-1 bg-[var(--color-bg-base)]"
              style={{ border: '2px solid var(--color-border)' }}
            >
              <option value="payroll">Employee Payroll</option>
              <option value="revenue">Revenue</option>
              <option value="variable_expenses">Variable Expenses</option>
            </select>
          </div>
          <button
            onClick={() => onRunAnalysis()}
            disabled={sensitivityLoading}
            className="w-full py-3 px-4 rounded-md font-bold disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {sensitivityLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Analyzing...
              </span>
            ) : 'Run Sensitivity Analysis'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SensitivityAnalysisCard;
